import eng from 'utils/locales/en.json';
import newDesignCopy from 'utils/locales/newDesign.json';
import { isNewDesignCopy } from 'utils/newDesignCopy';

import { createIntl, createIntlCache, IntlShape } from 'react-intl';
import { ClassifierData, ClassifierText, EntityClassifierValue } from 'types/classifiers';

interface LocaleRecord {
    [key: string]: string | LocaleRecord;
}

// react-intl requires single-level object
export const flattenMessages = (nestedMessages: LocaleRecord | undefined, prefix = '') => {
    if (nestedMessages === null || nestedMessages === undefined) {
        return {};
    }
    return Object.keys(nestedMessages).reduce((messages, key) => {
        const value = nestedMessages[key as keyof Locale];
        const prefixedKey = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string') {
            Object.assign(messages, { [prefixedKey]: value });
        } else {
            if (value) {
                Object.assign(messages, flattenMessages(value, prefixedKey));
            }
        }

        return messages;
    }, {});
};

export const translations: Record<string, string> = flattenMessages(eng);

/**
 * The catalog of the new design: `en.json` with the new design's own wording over the ids the classic design
 * also renders. `newDesign.json` rewords an id, it never adds one.
 */
export const newDesignTranslations: Record<string, string> = { ...translations, ...newDesignCopy };

/**
 * The locale the new design writes every number, amount and time in: a 24-hour clock, "1,234.56". Set here and, through
 * this, on the provider in `Locales`. Classic keeps `eng`, which resolves to US English.
 */
export const NEW_DESIGN_LOCALE = 'en-GB';

const classicIntl: IntlShape = createIntl({ locale: 'eng', messages: translations }, createIntlCache());
const newDesignIntl: IntlShape = createIntl(
    { locale: NEW_DESIGN_LOCALE, messages: newDesignTranslations },
    createIntlCache()
);

/** The catalog of the design in use, read at each call: a toast raised outside React follows a switch too. */
export const getIntl = (): IntlShape => (isNewDesignCopy() ? newDesignIntl : classicIntl);

export const getAllTranslatedMessages = (): string[] => [
    ...Object.values(translations),
    ...Object.values(newDesignCopy),
];

const NAME_LIST = new Intl.ListFormat(NEW_DESIGN_LOCALE, { type: 'conjunction' });

/** Names joined with no comma before "and", as the copy writes them: "A, B and C". */
export const joinNames = (names: string[]): string => NAME_LIST.format(names);

export const t = (id: string, values?: { [key: string]: string }): string => {
    return getIntl().formatMessage({ id }, { ...values });
};

export const getClassifierText = (
    classifier: ClassifierData | undefined,
    typeCode: string | undefined,
    overrideMap?: Record<string, string>,
    languageCode?: string
): string => {
    if (overrideMap && typeCode && overrideMap[typeCode]) {
        return overrideMap[typeCode];
    }

    const locale: string = languageCode ?? 'eng';
    const classifierValue: EntityClassifierValue | undefined = classifier?.values?.find(
        (value) => value.code === typeCode
    );

    return (
        classifierValue?.texts.find(
            (text: ClassifierText) => text.langCode.toLocaleLowerCase() === locale.toLocaleLowerCase()
        )?.value ||
        classifierValue?.texts.find((text: ClassifierText) => text.langCode.toLocaleLowerCase() === 'eng')?.value ||
        typeCode ||
        ''
    );
};

/**
 * The plural of a value's text, or the closest thing to one: the singular in the language asked for before the
 * English plural, so a screen never switches to English for one word of it, and the English singular last.
 */
export const getClassifierPluralText = (
    classifier: ClassifierData | undefined,
    typeCode: string | undefined,
    languageCode?: string
): string => {
    const locale: string = (languageCode ?? 'eng').toLocaleLowerCase();
    const texts: ClassifierText[] = classifier?.values?.find((value) => value.code === typeCode)?.texts ?? [];
    const textIn = (lang: string): ClassifierText | undefined =>
        texts.find((text: ClassifierText) => text.langCode.toLocaleLowerCase() === lang);
    const asked: ClassifierText | undefined = textIn(locale);
    const english: ClassifierText | undefined = textIn('eng');

    return asked?.textPlural || asked?.value || english?.textPlural || english?.value || typeCode || '';
};

export const getClassifierTextDescription = (
    classifier: ClassifierData | undefined,
    typeCode: string | undefined
): string => {
    const classifierValue: EntityClassifierValue | undefined = classifier?.values?.find(
        (value) => value.code === typeCode
    );

    return (
        classifierValue?.texts.find((text: ClassifierText) => text.langCode.toLocaleLowerCase() === 'eng')
            ?.description ||
        typeCode ||
        ''
    );
};
export const getClassifierDescription = (classifier: ClassifierData | undefined): string => {
    if (classifier?.values == undefined) {
        return '';
    }
    let classifierDescription = '';
    let classifierText;
    const sortedValues = classifier.values.toSorted((a, b) => a.code.localeCompare(b.code));
    sortedValues?.forEach((value) => {
        classifierText = value.texts.find((text) => text.langCode.toLocaleLowerCase() === 'eng');
        if (classifierText === undefined) {
            classifierDescription += `*N/A* - N/A\n`;
            return;
        }
        classifierDescription += `*${classifierText.value}* - ${classifierText.description}\n`;
    });
    return classifierDescription;
};
